import * as React from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { apiConfig } from "@/config/api";
import { ImageZoomModal } from "@/components/common/ImageZoomModal";

interface KtpPhotoCellProps {
  userId: string;
  idcardEncrypted: boolean;
  idcardUrl: string | null;
  userName?: string;
}

export function KtpPhotoCell({ userId, idcardEncrypted, idcardUrl, userName }: KtpPhotoCellProps) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [zoomOpen, setZoomOpen] = React.useState(false);

  React.useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const loadEncryptedImage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiConfig.baseURL}/api/users/${userId}/ktp-idcard`,
        { credentials: "include" }
      );
      if (!response.ok) {
        throw new Error("Failed to load KTP image");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load KTP");
      console.error("Error loading encrypted KTP:", err);
    } finally {
      setLoading(false);
    }
  };

  // Legacy (non-encrypted) image
  const legacySrc = idcardUrl
    ? idcardUrl.startsWith("data:") || idcardUrl.startsWith("http")
      ? idcardUrl
      : `data:image/jpeg;base64,${idcardUrl}`
    : null;

  // Encrypted image not loaded
  if (idcardEncrypted && !blobUrl && !error) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={loadEncryptedImage}
        disabled={loading}
        className="text-xs"
      >
        {loading ? (
          <>
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            Loading...
          </>
        ) : (
          "Lihat Foto KTP"
        )}
      </Button>
    );
  }

  // Encrypted image loaded or error
  if (idcardEncrypted) {
    if (error) {
      return <span className="text-xs text-destructive">Gagal muat</span>;
    }
    if (blobUrl) {
      return (
        <>
          <img
            src={blobUrl}
            alt="KTP"
            className="h-20 w-32 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setZoomOpen(true)}
          />
          <ImageZoomModal
            open={zoomOpen}
            onOpenChange={setZoomOpen}
            src={blobUrl}
            alt="KTP"
            title={userName ? `KTP - ${userName}` : "Foto KTP"}
          />
        </>
      );
    }
  }

  // Legacy image
  if (legacySrc) {
    return (
      <>
        <img
          src={legacySrc}
          alt="KTP"
          className="h-20 w-32 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setZoomOpen(true)}
        />
        <ImageZoomModal
          open={zoomOpen}
          onOpenChange={setZoomOpen}
          src={legacySrc}
          alt="KTP"
          title={userName ? `KTP - ${userName}` : "Foto KTP"}
        />
      </>
    );
  }

  // No image
  return <span className="text-muted-foreground text-xs">—</span>;
}
